import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef, useState, type FormEvent } from "react";
import { AuthFrame } from "@/components/marketing/auth-frame";
import { Button } from "@/components/ui/button";
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { forgotPasswordInput } from "@alm/validation";
import { authClient } from "@/lib/host/auth-client";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
});

/** Asks for a reset link. */
function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    // The same schema the rest of the application validates an address with,
    // so it is trimmed and lower-cased the one way. Better Auth looks the
    // address up exactly as given, and "Ada@Example.com " finds nobody.
    const parsed = forgotPasswordInput.safeParse({ email });
    if (!parsed.success) {
      setProblem("Enter the email address you sign in with, like name@example.com.");
      emailRef.current?.focus();
      return;
    }
    setProblem(null);
    setPending(true);
    // The client resolves with an error rather than throwing, and a thrown
    // network failure must not leave the button spinning forever.
    const result = await authClient
      .requestPasswordReset({ email: parsed.data.email })
      .catch(() => null);
    setPending(false);
    const status = result ? (result.error?.status ?? 200) : 0;
    if (status === 0 || status === 429 || status >= 500) {
      setProblem(
        status === 429
          ? "Too many requests. Wait a minute, then try again."
          : "Couldn't send the link. Check your connection and try again.",
      );
      return;
    }
    // The same answer whether or not the address has an account. Anything else
    // turns this form into a way of asking who works here.
    setSent(true);
  };

  return (
    <AuthFrame>
      <CardHeader className="items-center text-center">
        <CardTitle asChild className="text-xl">
          <h1>{sent ? "Check your email" : "Reset your password"}</h1>
        </CardTitle>
        <CardDescription>
          {sent
            ? "If that address has an account, a reset link is on its way."
            : "We will email you a link to choose a new one."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {sent ? (
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">
              The link works once and expires in an hour. If it doesn't arrive, check your spam
              folder and the address, then try again, or ask your administrator.
            </p>
            <Button asChild className="w-full" variant="outline">
              <Link to="/login">Back to sign in</Link>
            </Button>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={onSubmit}>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                ref={emailRef}
                autoComplete="email"
                enterKeyHint="send"
                id="email"
                required
                type="email"
                value={email}
                aria-invalid={problem ? true : undefined}
                aria-describedby={problem ? "email-problem" : undefined}
                onChange={(event) => setEmail(event.target.value)}
              />
              <p id="email-problem" role="alert" className="text-destructive text-sm empty:hidden">
                {problem}
              </p>
            </div>
            <Button className="w-full" disabled={pending} type="submit">
              {pending ? <Spinner /> : null}
              {pending ? "Sending…" : "Send reset link"}
            </Button>
            <Button asChild className="w-full" variant="ghost">
              <Link to="/login">Back to sign in</Link>
            </Button>
          </form>
        )}
      </CardContent>
    </AuthFrame>
  );
}
